import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

/**
 * Edge on the panel, so its titled band reads as the top of one box rather than a strip
 * floating over the section.
 *
 * Only the border lives here. A `Col` takes its width from the `border` prop by reading the
 * first space-separated part, and only when it starts with a digit, so a tokenised width
 * would be read as the style and drop the colour. Everything else this panel needs —
 * radius, overflow — is a layout prop.
 *
 * Hoisted so emotion sees one stable object rather than a new one each render.
 */
const PANEL_EDGE = {
  border: `${token('dimensions.border.width.100')} solid ${token('color.status.info-border')}`,
} as const;

interface DetailPanelProps {
  icon: JSX.Element;
  title: string;
  /** Shown in place of the rows when there are none. */
  emptyLabel: string;
  /**
   * Tells the reader the rows can be opened. Suppressed when there are none, so it never
   * points at a list that is not there.
   */
  hint?: string;
  children: React.ReactNode;
}

/**
 * Inline panel opened by a tile: titled band, then the rows.
 *
 * Carries no close control of its own. The tile that opened it is the control — clicking
 * it again closes the panel — so a second one would be a second way to do the same thing,
 * sited away from the state it changes.
 *
 * A tile reading zero is still worth opening — it is how the reader confirms there is
 * nothing outstanding — so the panel says so rather than opening blank.
 */
export function DetailPanel({
  icon,
  title,
  emptyLabel,
  hint,
  children,
}: DetailPanelProps): JSX.Element {
  // An empty array and an absent child both mean no rows to show.
  const hasRows = Array.isArray(children) ? children.length > 0 : !!children;
  return (
    <Col
      backgroundColor="color.surface.bg-default"
      borderRadius="dimensions.border.radius.200"
      // Clips the titled band's square corners to the panel's radius.
      overflow="hidden"
      minWidth={0}
      css={PANEL_EDGE}
    >
      <Row
        alignItems="center"
        gridGap="dimensions.spacing.200"
        padding="dimensions.spacing.200"
        backgroundColor="color.surface.bg-muted"
      >
        {icon}
        <Text fontWeight="bold" fontSize="T200">
          {title}
        </Text>
      </Row>
      <Col gridGap="dimensions.spacing.200" padding="dimensions.spacing.200" minWidth={0}>
        {hasRows && hint ? (
          <Text color="color.text.secondary" fontSize="T100">
            {hint}
          </Text>
        ) : null}
        {hasRows ? (
          children
        ) : (
          <Text color="color.text.secondary" fontSize="T200">
            {emptyLabel}
          </Text>
        )}
      </Col>
    </Col>
  );
}
